import { useState, useEffect } from "react";
import {
  AuthenticationProps,
  AuthenticationResponse,
} from "../types/authenticationTypes";
import useCloudFunctionCall from "@/hooks/useCloudFunctionCall";
import { createToast } from "@/utils/toaster/createToast";

interface UseVerificationCodeProps {
  baseAuthenticationProps: AuthenticationProps;
  action: string;
  onSuccess?: (response: AuthenticationResponse) => void;
}

export const useVerificationCode = ({
  baseAuthenticationProps,
  action,
  onSuccess,
}: UseVerificationCodeProps) => {
  const [code, setCode] = useState<{
    0: number | null;
    1: number | null;
    2: number | null;
    3: number | null;
    4: number | null;
    5: number | null;
  }>({
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
  });

  const [response, loading, error, authenticate] =
    useCloudFunctionCall<AuthenticationResponse>(
      "userAuthentication",
      undefined
    );

  const setFieldValue = (targetName: number, value: number | null) => {
    return setCode((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  const resendCode = async () => {
    await authenticate({
      ...baseAuthenticationProps,
      action: action === "verify-email" ? "resent-verification-code" : action,
    });
  };

  const verifyCode = async () => {
    let concatenatedString = "";

    Object.keys(code).forEach((key) => {
      const value = code[Number(key)];
      if (typeof value === "number") {
        concatenatedString += value.toString();
      }
    });

    await authenticate({
      ...baseAuthenticationProps,
      verificationCode: concatenatedString,
      action,
    });
  };

  // Auto run if code is 6 digits
  useEffect(() => {
    if (Object.values(code).every((value) => typeof value === "number")) {
      verifyCode();
    }
  }, [code]);

  // Handle responses
  useEffect(() => {
    if (
      !response ||
      !Object.values(code).every(
        (value) =>
          typeof value === "number" ||
          Object.values(code).every((value) => value === null)
      )
    )
      return;

    if (response.status === "verification-code-resent") {
      createToast({
        type: "info",
        message: response.payload,
      });
      return;
    }

    if (response.status === "email-verification-sent") {
      createToast({
        type: "success",
        message: response.payload,
      });
      return;
    }

    if (response.status === "user-verified") {
      createToast({
        type: "success",
        message: "Code accepted.",
      });
      if (onSuccess) {
        onSuccess(response);
      }
    } else {
      createToast({
        type: "error",
        message: response.payload,
      });
    }
  }, [response]);

  return {
    code,
    setCode,
    setFieldValue,
    loading,
    error,
    response,
    resendCode,
  };
};
